import { cloneDeep, omit } from 'lodash';
import { IsInt, IsNotEmpty, Max, Min, ValidateNested, ValidationArguments } from 'class-validator';
import { Type } from 'class-transformer';

import { Communicator } from './communicator';
import createCollectionResource from 'src/actions/shared/createResource';
import getTypeDependsOnWidgetType from 'src/models/helpers/getTypeDependsOnWidgetType';
import OfficeHours from 'src/models/BusinessHours/OfficeHours';

export enum CampaignType {
  VOICE = 'VOICE',
  SMS = 'SMS',
  EMAIL = 'EMAIL',
}

class SmsCampaignContent {
  @IsNotEmpty({ message: 'Language is not selected' })
  answerLanguage: string;

  @IsNotEmpty({ message: 'Please select an Answer' })
  answerId: string;

  @IsNotEmpty({ message: 'Please select a phone number' })
  endpointId: string;
}

class EmailCampaignContent {
  @IsNotEmpty({ message: 'Language is not selected' })
  answerLanguage: string;

  @IsNotEmpty({ message: 'Please select an Answer' })
  answerId: string;

  @IsNotEmpty({ message: 'Please select an email address' })
  endpointId: string;
}

class VoiceCampaignContent {
  @IsInt({
    message: (args: ValidationArguments) => {
      if (!args.value) {
        return 'value must be between 1 and 30';
      }
      return 'enter a whole number';
    },
  })
  @Min(1, { message: 'value must be between 1 and 30' })
  @Max(30, { message: 'value must be between 1 and 30' })
  concurrency: number;

  @IsNotEmpty({ message: 'Please select an IVR' })
  ivrId: string;

  @IsNotEmpty({ message: 'Please select a phone number' })
  endpointId: string;
}

const campaignTypeToClass = {
  [CampaignType.VOICE]: VoiceCampaignContent,
  [CampaignType.SMS]: SmsCampaignContent,
  [CampaignType.EMAIL]: EmailCampaignContent,
};

export class Campaign {
  id: string;

  @IsNotEmpty({ message: 'Please enter the Campaign name' })
  name: string;

  @IsNotEmpty({ message: 'Please enter the description' })
  description: string;

  @ValidateNested()
  @Type(() => Communicator)
  createdBy: Communicator;

  createdAt: string;

  @IsNotEmpty()
  type: CampaignType;

  @ValidateNested()
  @Type(getTypeDependsOnWidgetType(['type'], ([type]) => campaignTypeToClass[type]))
  content: VoiceCampaignContent | SmsCampaignContent | EmailCampaignContent;

  @Type(() => OfficeHours)
  officeHours: OfficeHours;

  version: number;
}

const campaignsResource = createCollectionResource<Campaign>({
  name: 'campaigns',
  endpoint: 'v1/orgs/:orgId/campaigns/:id',
  type: Campaign,
  options: {
    transientAttrsFn: <Campaign>(campaign: Partial<Campaign>): Campaign =>
      omit(cloneDeep(campaign), ['createdAtString', 'createdByString', 'createdAt', 'updatedAt']) as Campaign,
    toastMessages: {
      onAdd: campaign => `"${campaign.name}" has been created.`,
      onUpdate: campaign => `"${campaign.name}" has been updated.`,
      onRemove: () => `Deleted campaign`,
    },
    versionedUpdates: true,
  },
});

export default campaignsResource;
